// src/views/admin/offers/component/AddEditDrawer.jsx
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';

import { Input } from '@/components/ui/inputs/input';
import InputRadio from '@/components/ui/inputs/input-radio';
import { InputDatepicker } from '@/components/ui/inputs/input-datepicker';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import ToastMe from '@/components/ui/ToastMe';

import { addOfferService, updateOfferService, getByIdOfferService } from '@/services/OfferServices';
import { fileUpload } from '@/services/CommonService';
import validationErrors from '@/lib/validationErrors';

const DEFAULTS = {
  name: '',
  code: '',
  discount_per: '',
  start_date: '',
  end_date: '',
  file_name: '',
  status: '1', // keep radios as strings to stay controlled
};

const AddEditDrawer = ({ titleText, activeDrawer, setActiveDrawer, getTableData, editDataId = '' }) => {
  const dispatch = useDispatch();

  // Pull tenant context (fallback to localStorage like your axios boot does)
  const companyFromStore = useSelector((s) => s.Auth?.data?.data?.company_id);
  const branchFromStore  = useSelector((s) => s.Branch?.active?._id);
  const company_id = companyFromStore || (typeof localStorage !== 'undefined' ? localStorage.getItem('company_id') : '');
  const branch_id  = branchFromStore  || (typeof localStorage !== 'undefined' ? localStorage.getItem('branch_id')  : '');

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULTS });

  const [itemStatus, setItemStatus] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormSumbmit, setIsFormSumbmit] = useState(false);

  // When drawer opens, reset + optionally load edit record
  useEffect(() => {
    if (!activeDrawer) return;
    reset(DEFAULTS);
    setItemStatus(1);

    if (editDataId) {
      getDataById(editDataId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawer, editDataId]);

  const handleDrawerClose = () => {
    reset(DEFAULTS);
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    getTableData?.();
  };

  const handleChangeStatus = (event) => {
    const value = event?.target?.value ?? event?.value ?? 1; // "1" | "2"
    setValue('status', String(value));
    setItemStatus(Number(value));
  };

  const getDataById = (id) => {
    setIsLoading(true);
    dispatch(getByIdOfferService(id))
      .then((res) => {
        const d = res?.data?.data || {};

        // Robust mapping from API → form
        const mapped = {
          name: d.name ?? '',
          code: d.code ?? '',
          discount_per: d.discount_per ?? '',
          // If API returns ISO date strings/Date objects, store them as-is; the datepicker component
          // already uses selected={getValues('start_date')} and onChange sets raw date back.
          start_date: d.start_date ? new Date(d.start_date) : '',
          end_date: d.end_date ? new Date(d.end_date) : '',
          file_name: d.file_name ?? '',
          status: String(d.status ?? '1'),
        };

        reset(mapped);
        setItemStatus(Number(mapped.status));
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      ToastMe('File is not valid', 'error');
      return;
    }
    if (!file.type?.includes('image/')) {
      ToastMe('Please upload only image files', 'error');
      return;
    }

    dispatch(fileUpload({ file }, 'offer'))
      .then((res) => {
        const out = res?.data;
        if (out?.fileName) setValue('file_name', out.fileName, { shouldDirty: true });
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
      });
  };

  const onSubmit = (data) => {
    setIsFormSumbmit(true);

    // Company is required server-side (schema + tenant plugin)
    const cid = company_id && String(company_id).trim();
    if (!cid) {
      ToastMe('No company selected. Please select a company before saving.', 'error');
      setIsFormSumbmit(false);
      return;
    }

    // Normalize payload
    const payload = {
      ...data,
      company_id: cid,
      ...(branch_id && String(branch_id).toLowerCase() !== 'all' ? { branch_id } : {}),
      discount_per: Number(data.discount_per ?? 0),
      status: Number(data.status ?? 1),
      // Dates: keep as Date objects/ISO; your controller uses moment to parse when filtering.
      start_date: data.start_date || '',
      end_date: data.end_date || '',
    };

    const thunk = editDataId
      ? updateOfferService(editDataId, payload)
      : addOfferService(payload);

    dispatch(thunk)
      .then(() => {
        reset(DEFAULTS);
        setItemStatus(1);
        setIsFormSumbmit(false);
        handleDrawerClose();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsFormSumbmit(false);
      });
  };

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={titleText}
      activeDrawer={activeDrawer}
      handleDrawerClose={handleDrawerClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Name */}
          <Controller
            control={control}
            rules={{ required: true, minLength: 2, maxLength: 50 }}
            name="name"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                size="sm"
                labelText="Name"
                type="text"
                labelStyleClassName="db-field-title required"
                errorData={{ minLength: '2', maxLength: '50' }}
                errorType={errors?.name?.type}
              />
            )}
          />

          {/* Code (optional) */}
          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                size="sm"
                labelText="Code (optional)"
                type="text"
                labelStyleClassName="db-field-title"
                errorType={errors?.code?.type}
              />
            )}
          />

          {/* Discount % */}
          <Controller
            control={control}
            rules={{ required: true, min: 0, max: 100 }}
            name="discount_per"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                size="sm"
                labelText="Discount Percentage"
                type="number"
                step="0.01"
                labelStyleClassName="db-field-title required"
                errorData={{ min: '0', max: '100' }}
                errorType={errors?.discount_per?.type}
              />
            )}
          />

          {/* Start Date */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="start_date"
            render={({ field }) => (
              <InputDatepicker
                {...field}
                onChange={(date) => setValue('start_date', date)}
                selected={getValues('start_date')}
                size="sm"
                labelText="Start Date"
                type="date"
                id="start_date"
                labelStyleClassName="db-field-title required"
                errorType={errors?.start_date?.type}
              />
            )}
          />

          {/* End Date */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="end_date"
            render={({ field }) => (
              <InputDatepicker
                {...field}
                onChange={(date) => setValue('end_date', date)}
                selected={getValues('end_date')}
                size="sm"
                labelText="End Date"
                type="date"
                id="end_date"
                labelStyleClassName="db-field-title required"
                errorType={errors?.end_date?.type}
              />
            )}
          />

          {/* Image upload (uncontrolled) */}
          <div className="form-input-item">
            <Input
              onChange={handleFileUpload}
              size="sm"
              labelText="Image"
              type="file"
              id="tmp_file_name"
            />
          </div>

          {/* Status */}
          <div className="form-input-item">
            <label className="db-field-title required" htmlFor="active">
              Status
            </label>
            <div className="db-field-radio-group">
              <InputRadio
                id="active"
                name="status"
                labelText="Active"
                value="1"
                onChange={handleChangeStatus}
                checked={itemStatus === 1}
              />
              <InputRadio
                id="inactive"
                name="status"
                labelText="Inactive"
                value="2"
                onChange={handleChangeStatus}
                checked={itemStatus === 2}
              />
            </div>
          </div>
        </div>

        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="submit"
              className="db-btn py-2 text-white bg-primary"
              disabled={isFormSumbmit || isLoading}
            >
              <i className="lab lab-save"></i>
              <span>Save</span>
              {isFormSumbmit && (
                <svg
                  className="animate-spin -ml-1 ml-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
            </button>

            <button
              type="button"
              className="modal-btn-outline modal-close"
              onClick={handleDrawerClose}
            >
              <i className="lab lab-close"></i>
              <span>Close</span>
            </button>
          </div>
        </div>
      </form>
    </SidebarDrawer>
  );
};

export default AddEditDrawer;
